'use client'

import type { LandingBorrowItem } from '@/app/api/landing/markets/types'
import { hyperEVM } from '@/config/chain/constants'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Typography } from '@/ui/atoms/typography/Typography'
import Link from 'next/link'

interface BorrowCardContentProps {
  items: LandingBorrowItem[]
  isLoading: boolean
}

function getDetailPath(item: LandingBorrowItem): string {
  if (item.venue === 'pooled') {
    return `/markets/pooled/${hyperEVM.id}/${item.assetAddress}`
  }

  return `/borrow/explore?${new URLSearchParams({
    debt: item.symbol,
    ...(item.marketLabel ? { markets: item.marketLabel } : {}),
  }).toString()}`
}

export function BorrowCardSkeleton() {
  return (
    <div className="-mx-2 flex flex-col gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-1.5">
          <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-10 rounded" />
            </div>
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-5 w-14" />
        </div>
      ))}
    </div>
  )
}

export function BorrowCardContent({ items, isLoading }: BorrowCardContentProps) {
  if (isLoading) {
    return <BorrowCardSkeleton />
  }

  return (
    <div className="-mx-2 flex flex-col gap-4">
      {items.map((item) => (
        <Link
          key={item.id}
          href={getDetailPath(item)}
          onClick={(event) => event.stopPropagation()}
          className="flex items-center gap-3 rounded-sm px-2 py-1.5 transition-colors hover:bg-white/5"
        >
          <TokenImage
            src={item.tokenImageUrl}
            alt={item.symbol}
            width={32}
            height={32}
            className="h-8 w-8 shrink-0 rounded-full"
            fallbackSrc={item.fallbackLogoURI}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-semibold text-sm text-white">{item.symbol}</span>
              {item.marketLabel && (
                <span className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {item.marketLabel}
                </span>
              )}
            </div>
            {item.maxLTV > 0 && (
              <span className="text-[11px] text-muted-foreground">LTV {item.maxLTV.toFixed(0)}%</span>
            )}
          </div>
          <Typography variant="span" gradient className="font-bold text-lg" style={{ paddingBottom: '0' }}>
            {item.bestBorrowAPY.toFixed(2)}%
          </Typography>
        </Link>
      ))}
    </div>
  )
}
